import { create } from 'zustand';
import { exportTasksApi, type CreateExportTaskPayload, type ExportTask } from '../api/exportTasks';
import { pageKeyFromRoute, trackFeatureAction } from '../lib/usageTracking';

export type DownloadTaskStatus = 'queued' | 'processing' | 'ready' | 'failed';

export interface DownloadTask {
  id: string;
  title: string;
  fileName: string;
  status: DownloadTaskStatus;
  createdAt: number;
  completedAt?: number;
  downloaded?: boolean;
  url?: string;
  error?: string;
}

interface DownloadCenterState {
  tasks: DownloadTask[];
  page: number;
  pageSize: number;
  total: number;
  loading: boolean;
  unseenCount: number;
  autoDownloadTaskIDs: string[];
  enqueueReportExport: (payload: CreateExportTaskPayload) => Promise<string>;
  fetchTasks: (page?: number) => Promise<void>;
  setPage: (page: number) => Promise<void>;
  markVisibleTasksSeen: () => void;
  refreshTasks: (brandId?: string) => Promise<void>;
  downloadTask: (id: string) => Promise<void>;
  removeTask: (id: string) => Promise<void>;
  clearFinished: () => Promise<void>;
}

const MAX_TASKS = 20;
const DEFAULT_PAGE_SIZE = 10;
const SEEN_DOWNLOAD_TASKS_KEY = 'download_center_seen_task_ids';
let currentBrandId: string | undefined;

function toTime(value?: string): number | undefined {
  if (!value) return undefined;
  const ts = new Date(value).getTime();
  return Number.isFinite(ts) ? ts : undefined;
}

function mapServerTask(task: ExportTask): DownloadTask {
  return {
    id: task.id,
    title: task.title,
    fileName: task.file_name,
    status: task.status,
    createdAt: toTime(task.created_at) ?? Date.now(),
    completedAt: toTime(task.completed_at),
    downloaded: Boolean(task.downloaded_at),
    url: task.file_url,
    error: task.error,
  };
}

function readSeenTaskIds(): Set<string> {
  try {
    const raw = localStorage.getItem(SEEN_DOWNLOAD_TASKS_KEY);
    const ids = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(ids) ? ids.filter(id => typeof id === 'string') : []);
  } catch {
    return new Set();
  }
}

function writeSeenTaskIds(ids: Set<string>) {
  localStorage.setItem(SEEN_DOWNLOAD_TASKS_KEY, JSON.stringify(Array.from(ids).slice(-300)));
}

function countUnseenTasks(tasks: DownloadTask[]): number {
  const seenIds = readSeenTaskIds();
  return tasks.filter(task => task.status === 'ready' || task.status === 'failed').filter(task => !seenIds.has(task.id)).length;
}

export const useDownloadCenterStore = create<DownloadCenterState>()((set, get) => ({
  tasks: [],
  page: 1,
  pageSize: DEFAULT_PAGE_SIZE,
  total: 0,
  loading: false,
  unseenCount: 0,
  autoDownloadTaskIDs: [],
  enqueueReportExport: async (payload) => {
    const route = window.location.pathname;
    try {
      const res = await exportTasksApi.create(payload);
      const task = mapServerTask(res.data);
      set(state => {
        const nextTasks = [task, ...state.tasks.filter(item => item.id !== task.id)].slice(0, MAX_TASKS);
        return {
          page: 1,
          tasks: nextTasks.slice(0, state.pageSize),
          total: Math.max(state.total + 1, nextTasks.length),
          unseenCount: countUnseenTasks(nextTasks),
          autoDownloadTaskIDs: state.autoDownloadTaskIDs.includes(task.id)
            ? state.autoDownloadTaskIDs
            : [task.id, ...state.autoDownloadTaskIDs].slice(0, MAX_TASKS),
        };
      });
      void trackFeatureAction({
        page_key: pageKeyFromRoute(route),
        feature_key: 'report_export',
        action: 'export',
        route,
        properties: { report_key: payload.report_key, title: payload.title, file_name: payload.file_name },
      });
      return task.id;
    } catch (error) {
      void trackFeatureAction({
        page_key: pageKeyFromRoute(route),
        feature_key: 'report_export',
        action: 'export',
        route,
        outcome: 'failed',
        properties: { report_key: payload.report_key, title: payload.title },
      });
      throw error;
    }
  },
  fetchTasks: async (page) => {
    const nextPage = Math.max(1, page ?? get().page);
    const { pageSize } = get();
    set({ loading: true });
    try {
      const res = await exportTasksApi.list(currentBrandId);
      const allTasks = (res.data ?? []).map(mapServerTask).slice(0, MAX_TASKS);
      const total = allTasks.length;
      const maxPage = Math.max(1, Math.ceil(total / pageSize));
      const normalizedPage = Math.min(nextPage, maxPage);
      const start = (normalizedPage - 1) * pageSize;
      set({
        tasks: allTasks.slice(start, start + pageSize),
        page: normalizedPage,
        total,
        unseenCount: countUnseenTasks(allTasks),
        loading: false,
      });
    } catch {
      set({ loading: false });
    }
  },
  setPage: async (page) => {
    await get().fetchTasks(page);
  },
  markVisibleTasksSeen: () => {
    const seenIds = readSeenTaskIds();
    get().tasks.forEach(task => {
      if (task.status === 'ready' || task.status === 'failed') {
        seenIds.add(task.id);
      }
    });
    writeSeenTaskIds(seenIds);
    set({ unseenCount: 0 });
  },
  refreshTasks: async (brandId) => {
    currentBrandId = brandId;
    await get().fetchTasks(1);
  },
  downloadTask: async (id) => {
    const task = get().tasks.find(item => item.id === id);
    if (!task || task.status !== 'ready') return;
    try {
      // Ready tasks already include their direct object URL. Use it immediately so
      // a user click remains a browser download gesture instead of waiting for the
      // bookkeeping request that records downloaded_at.
      let url = task.url;
      if (!url) {
        const res = await exportTasksApi.download(id);
        url = res.data?.url;
      } else {
        void exportTasksApi.download(id).catch(() => undefined);
      }
      if (!url) throw new Error('下载地址为空，请重新生成导出文件。');
      const link = document.createElement('a');
      link.href = url;
      link.download = task.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      set(state => ({
        tasks: state.tasks.map(item => (item.id === id ? { ...item, downloaded: true, error: undefined } : item)),
        autoDownloadTaskIDs: state.autoDownloadTaskIDs.filter(taskID => taskID !== id),
      }));
    } catch (error) {
      const message = error instanceof Error ? error.message : '下载失败，请稍后重试。';
      set(state => ({
        tasks: state.tasks.map(item => (item.id === id ? { ...item, error: message } : item)),
        autoDownloadTaskIDs: state.autoDownloadTaskIDs.filter(taskID => taskID !== id),
      }));
    }
  },
  removeTask: async (id) => {
    const task = get().tasks.find(item => item.id === id);
    if (!task || task.status === 'queued' || task.status === 'processing') return;
    await exportTasksApi.remove(id);
    set(state => ({
      tasks: state.tasks.filter(item => item.id !== id),
      total: Math.max(0, state.total - 1),
      autoDownloadTaskIDs: state.autoDownloadTaskIDs.filter(taskID => taskID !== id),
    }));
    await get().fetchTasks(get().page);
  },
  clearFinished: async () => {
    const finished = get().tasks.filter(task => task.status === 'ready' || task.status === 'failed');
    await Promise.all(finished.map(task => exportTasksApi.remove(task.id)));
    await get().fetchTasks(1);
  },
}));
